import React, { useState } from 'react';
import {
    Modal,
    Typography,
    Grid,
    Paper,
    Box,
    IconButton,
    TextField,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tabs,
    Tab,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
  } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '85%',
  height: '85vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'hidden',
};

const contentContainerStyle = {
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
};

// Componente para el panel de pestañas
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ 
        height: '100%',
        display: value === index ? 'block' : 'none' 
      }}
      {...other}
    >
      {value === index && (
        <Box sx={{ 
          p: 3, 
          height: '100%',
          minHeight: '500px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DetalleNormativaClienteModal = ({ open, onClose, normativa }) => {
  console.log('normativa en detalle modal:', normativa);
  const [isLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (isLoading || !normativa) {
    return null;
  }

  // Verificar si hay compromisos u obligaciones
  const tieneCompromisos = normativa.compromisos && normativa.compromisos.length > 0;
  const tieneObligaciones = normativa.obligaciones && normativa.obligaciones.length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
    >
      <Box sx={style}>
        {/* Header con título */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2,
          flexShrink: 0
        }}>
          <Typography variant='h6'>Detalle de normativa</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs fijas */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          flexShrink: 0
        }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="detalle normativa tabs">
            <Tab label="Información General" />
            {tieneCompromisos && <Tab label={`Compromisos (${normativa.compromisos.length})`} />}
            {tieneObligaciones && <Tab label={`Obligaciones (${normativa.obligaciones.length})`} />}
          </Tabs>
        </Box>

        {/* Contenido con scroll */}
        <Box sx={contentContainerStyle}>
          {/* Tab Panel 0: Información General */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2} sx={{ height: 'fit-content' }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={normativa.nombre || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Código"
                  value={normativa.codigo || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tipo de Documento"
                  value={normativa.tipoDocumento || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Organismo Emisor"
                  value={normativa.organismoEmisor || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Estado de Tramitación"
                  value={normativa.estadoTramitacion || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fecha de Publicación"
                  value={normativa.fechaPublicacion ? new Date(normativa.fechaPublicacion).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  }) : ''}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL"
                  value={normativa.url || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Descripción"
                  value={normativa.descripcion || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Paper elevation={3} sx={{ p: 2, height: '300px', overflow: 'auto' }}>
                  <Typography variant="subtitle1" gutterBottom>Aplica a</Typography>
                  <List dense>
                    {normativa.clasificacionDocumentos?.map((clasificacion) => (
                      <ListItem key={clasificacion.id} alignItems="flex-start">
                        <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                          <FiberManualRecordIcon sx={{ fontSize: '0.8rem' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={clasificacion.nombre}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { wordBreak: 'break-word' }
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Paper elevation={3} sx={{ p: 2, height: '300px', overflow: 'auto' }}>
                  <Typography variant="subtitle1" gutterBottom>Rubros</Typography>
                  <List dense>
                    {normativa.rubros?.map((rubro) => (
                      <ListItem key={rubro.id} alignItems="flex-start">
                        <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                          <FiberManualRecordIcon sx={{ fontSize: '0.8rem' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={rubro.nombre}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { wordBreak: 'break-word' }
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab Panel: Compromisos (índice dinámico) */}
          {tieneCompromisos && (
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" component="h2" sx={{ mb: 2, flexShrink: 0 }}>
                  Compromisos de {normativa.nombre}
                </Typography>

                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Descripción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {normativa.compromisos.map((compromiso) => (
                        <TableRow key={compromiso.id}>
                          <TableCell>{compromiso.descripcion}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </TabPanel>
          )}

          {/* Tab Panel: Obligaciones (índice dinámico) */}
          {tieneObligaciones && (
            <TabPanel value={tabValue} index={tieneCompromisos ? 2 : 1}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" component="h2" sx={{ mb: 2, flexShrink: 0 }}>
                  Obligaciones de {normativa.nombre}
                </Typography>

                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Descripción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {normativa.obligaciones.map((obligacion) => (
                        <TableRow key={obligacion.id}>
                          <TableCell>{obligacion.descripcion}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </TabPanel>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default DetalleNormativaClienteModal;